import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";

import {
  APP_ENV,
  APP_VERSION,
  DATADOG_CLIENT_TOKEN,
  DATADOG_RUM_APPLICATION_ID,
  DATADOG_SERVICE,
  DATADOG_SITE,
  IS_TEST,
  MOCKING_ENABLED,
} from "@src/env";

/**
 * Initializes the Datadog browser logs SDK.
 *
 * Logs are tagged with `service: stitch-web` (overridable via
 * `VITE_DATADOG_SERVICE`) so they correlate with the existing
 * `service:stitch-web` APM service in Datadog.
 *
 * Initialization is skipped when:
 *   - `VITE_DATADOG_CLIENT_TOKEN` is not set (e.g. local dev without secrets)
 *   - MSW mocking is enabled (avoids polluting Datadog with mock-app traffic)
 *   - Running in the test environment
 *
 * Calling `datadogLogs.init` more than once is a no-op in the SDK, but we
 * still guard with the early return above to keep startup fast.
 */
function initDatadogLogs(): void {
  if (IS_TEST) return;
  if (MOCKING_ENABLED) return;
  if (!DATADOG_CLIENT_TOKEN) return;
  if (typeof window === "undefined") return;

  datadogLogs.init({
    clientToken: DATADOG_CLIENT_TOKEN,
    site: DATADOG_SITE,
    service: DATADOG_SERVICE,
    env: APP_ENV,
    version: APP_VERSION,
    forwardErrorsToLogs: false,
    forwardConsoleLogs: [],
    sessionSampleRate: 100,
    allowedTrackingOrigins: [
      /\.elationemr\.com$/,
      /el8-.*\.internal/,
      /^http:\/\/localhost/,
    ],
  });

  datadogLogs.logger.setHandler(["console", "http"]);
  datadogLogs.logger.setLevel("info");
}

/**
 * Initializes the Datadog Real User Monitoring (RUM) SDK.
 *
 * RUM tracks page views, user actions, frontend errors, resource timings,
 * and long tasks. Sessions are tagged with `service: stitch-web` so they
 * correlate with the existing APM and browser-logs streams in Datadog.
 *
 * Initialization is skipped when:
 *   - `VITE_DATADOG_CLIENT_TOKEN` or `VITE_DATADOG_RUM_APPLICATION_ID` is
 *     not set (e.g. local dev without secrets)
 *   - MSW mocking is enabled (avoids polluting Datadog with mock-app traffic)
 *   - Running in the test environment
 *
 * Session replay is enabled at a reduced sample rate to keep recording
 * volume manageable while still surfacing representative user sessions.
 */
function initDatadogRum(): void {
  if (IS_TEST) return;
  if (MOCKING_ENABLED) return;
  if (!DATADOG_CLIENT_TOKEN) return;
  if (!DATADOG_RUM_APPLICATION_ID) return;
  if (typeof window === "undefined") return;

  datadogRum.init({
    applicationId: DATADOG_RUM_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    site: DATADOG_SITE,
    service: DATADOG_SERVICE,
    env: APP_ENV,
    version: APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    defaultPrivacyLevel: "mask-user-input",
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    allowedTrackingOrigins: [
      /\.elationemr\.com$/,
      /el8-.*\.internal/,
      /^http:\/\/localhost/,
    ],
  });
}

initDatadogLogs();
initDatadogRum();

export { datadogLogs, datadogRum };
